import { useSyncExternalStore } from 'react';
import type { IShellKeyboardEvent, IShellKeyboardMovingEvent } from '@featit/shared';
import { onShellEvent } from '@/shell/shellBridge';
import { hasShellCapability, isShellRuntime } from '@/shell/shellDetection';

/**
 * IU-3b (`docs/concept-iphone-ux.md#iu-3b-the-sheet-primitive-and-the-keyboard`, S-10, M-9): how
 * much of the page the on-screen keyboard covers, and how a docked bar should move to follow it.
 *
 * **One strategy, the visual viewport.** The web view's frame never changes for the keyboard (in
 * the app or in mobile Safari), so the keyboard is read as the gap between the layout viewport's
 * bottom and the visual viewport's: `innerHeight − visualViewport.height − visualViewport.offsetTop`,
 * updated on the visual viewport's `resize` and `scroll`. That is the code path everywhere.
 *
 * **The app adds the curve.** A shell with `keyboardFrames` sends `keyboard { height, duration,
 * curve }` from `keyboardWillChangeFrame`, before the keyboard moves. The inset takes that height at
 * once, and the move runs with the keyboard's own duration and curve, so a bar rides with it. Mobile
 * Safari has no such event: there the bar moves when the visual viewport reports, with 250 ms
 * ease-out — a step behind the keyboard, which is the one honest difference.
 *
 * **Two numbers, because WebKit can pan instead of scroll** (I-1145). Focusing a field near the end
 * of a page the document cannot scroll far enough for, WebKit pans the layout viewport up under the
 * keyboard instead: measured on iOS 26.5 in the app, D-0001's page scrolled to its Note, the comment
 * field focused — `innerHeight` 874, `visualViewport.height` 471, `offsetTop` 403. Fixed elements
 * move up with the layout viewport, so a bar at its bottom already sits on the keyboard and must
 * rise by `874 − 471 − 403 = 0`, while the keyboard still covers 403. `covered` says whether the
 * keyboard is up (the screen it takes), and `inset` how far a bar fixed to the bottom must rise to
 * sit on it; reading one for the other drew the bar 83 pt above the keyboard (the tab bar's resting
 * place, kept by a `max` with the resting inset) or, when the shell's keyboard height landed after
 * the pan, a whole keyboard higher — over the status bar.
 *
 * **Dismissal in mobile Safari.** The app dismisses on a drag (`keyboardDismissMode = .onDrag`). A
 * browser cannot, so while the keyboard is up, a drag down the page of more than 10 px blurs the
 * focused field, and the keyboard goes with its own animation.
 *
 * **The app that moves its web view lifts nothing** (I-1187/I-1145/I-1203, `keyboardResize`). The
 * shell pins the web view's bottom to the keyboard's top every frame of a rise (I-1556), so a `bottom: 0`
 * bar already sits on the keyboard: the inset is 0, and `keyboard { height }` only says the keyboard
 * is up (`covered`) — the tab bar is under it, so a docked bar's resting lift is 0 too. Measured on
 * iOS 26.5 with the web view left at full height: WebKit scrolled the page to reveal the focused
 * field and re-anchored every fixed element 403 pt up, so a lift from here fought WebKit's own.
 *
 * Read it through `useKeyboardInset()`. `DockedBar` and `Sheet` do; the floating ＋ hides on
 * `inset > 0` (IU-3).
 */
export interface IKeyboardInset {
  /** CSS px a bar fixed to the layout viewport's bottom rises to sit on the keyboard; 0 while it is
   *  down, and 0 too when WebKit has panned the layout viewport up for it (`covered` says which). */
  readonly inset: number;
  /** CSS px of the screen the keyboard takes, 0 while it is down: whether it is up. */
  readonly covered: number;
  /** The `transition` a docked bar's `transform` runs with to follow it. */
  readonly transition: string;
  /**
   * In the app that moves its web view (`keyboardResize`), where things are from the web view's
   * top, never from its bottom: `full` is its height before the keyboard rose, `keyboardTop` where
   * the keyboard's top is this frame (`full` while it is down). A bar placed from these does not
   * move when the web view's height changes, in whichever frame the page sees that. `null` anywhere
   * else.
   */
  readonly place: { readonly full: number; readonly keyboardTop: number } | null;
}

/** Mobile Safari's step: the visual viewport reports, and the bar follows. */
export const VISUAL_VIEWPORT_TRANSITION = 'transform 250ms ease-out';

/** The keyboard's own curve (`UIView.AnimationCurve` 7, private), as close as CSS draws it. */
const KEYBOARD_CURVE = 'cubic-bezier(0.38, 0.7, 0.125, 1)';

/** A blur on a drag down the page, in mobile Safari, once the finger has moved this far. */
const DISMISS_DRAG_PX = 10;

/** `UIView.AnimationCurve`'s raw value as a CSS timing function. */
export function cssTimingOfCurve(curve: number): string {
  switch (curve) {
    case 0:
      return 'ease-in-out';
    case 1:
      return 'ease-in';
    case 2:
      return 'ease-out';
    case 3:
      return 'linear';
    default:
      // 7, the keyboard's, and any curve a later iOS introduces for it.
      return KEYBOARD_CURVE;
  }
}

/** The transition a `keyboard` event asks for: its duration and its curve, on `transform`. */
export function transitionOfKeyboardEvent(event: IShellKeyboardEvent): string {
  return `transform ${Math.round(event.duration * 1000)}ms ${cssTimingOfCurve(event.curve)}`;
}

/** The keyboard's inset from the visual viewport, in whole px, never negative. */
export function visualViewportInset(): number {
  if (typeof window === 'undefined') return 0;
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  return Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
}

/** The screen the keyboard takes, from the visual viewport: wherever WebKit has panned it. */
function visualViewportCovered(): number {
  if (typeof window === 'undefined') return 0;
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  return Math.max(0, Math.round(window.innerHeight - viewport.height));
}

/** How far WebKit has panned the layout viewport up under the visual one, now. */
function visualViewportPan(): number {
  return Math.max(0, Math.round(window.visualViewport?.offsetTop ?? 0));
}

let snapshot: IKeyboardInset = {
  inset: 0,
  transition: VISUAL_VIEWPORT_TRANSITION,
  covered: 0,
  place: null,
};
/** Whether the shell moves the web view for the keyboard (`keyboardResize`), read once attached. */
let webViewMoves = false;
/** Until when a shell `keyboard` event's curve, rather than the visual viewport's, moves the bar. */
let shellCurveUntil = 0;
const listeners = new Set<() => void>();
let detach: (() => void) | null = null;

function publish(next: IKeyboardInset): void {
  if (
    next.inset === snapshot.inset &&
    next.transition === snapshot.transition &&
    next.covered === snapshot.covered &&
    next.place?.full === snapshot.place?.full &&
    next.place?.keyboardTop === snapshot.place?.keyboardTop
  ) {
    return;
  }
  snapshot = next;
  for (const listener of [...listeners]) listener();
}

function followVisualViewport(): void {
  // The web view itself ends at the keyboard: there is no gap to read, and none to lift for — but a
  // pan of the layout viewport moves where the bar must be drawn (I-1235), outside a motion too.
  if (webViewMoves) {
    // Placed again now, in the frame the pan is drawn in, whether or not a motion's frames run.
    cancelAnimationFrame(motionFrame);
    followMovedWebView();
    return;
  }
  const transition =
    performance.now() < shellCurveUntil ? snapshot.transition : VISUAL_VIEWPORT_TRANSITION;
  const inset = visualViewportInset();
  // Up even when WebKit panned the page for it and the inset is 0 (I-1145): the screen the
  // keyboard takes, wherever the pan put the layout viewport.
  publish({ inset, transition, covered: visualViewportCovered(), place: null });
}

/*
  I-1187: the app that moves its web view moves it with the keyboard, but the page lays out at its
  new height at once, so the web places the keyboard's top itself, frame by frame
  (`keyboardTopAt`), from the web view's top — whichever of the event and the new height reaches
  the page first.

  **When.** The keyboard starts a variable time after `keyboard` arrives: measured on iOS 26.5
  (simq-kb, kb31f) at about 16, 35 and 70 ms. Played from `keyboard`'s arrival, the bar ran up to
  57 pt ahead of the keyboard. So the motion starts when the shell sees the keyboard move
  (`keyboardMoving`, with how far it had got), and only if that never comes, `KEYBOARD_MOVING_WAIT_MS`
  after `keyboard`.

  **How.** The keyboard's own measured motion (`KEYBOARD_MOTION`: progress by ms since it started
  moving, the same for the rise and the hide, which also moves although it says 0 s), never a
  bezier fitted to a curve number.
*/

/** How long the web waits for `keyboardMoving` before it starts the motion itself: past the
 *  latest start measured (about 70 ms), with a frame to spare. */
export const KEYBOARD_MOVING_WAIT_MS = 100;

/**
 * I-1556 (joker, list 11): how long after the shell set the web view's bottom the page sees its new
 * height (`resize`): the layout round trip, two display frames. Measured as the drop's lag: the bar
 * placed on the page's end trailed the keyboard by about 67 ms. The keyboard's place from a page end
 * is the place that page end was set at, this much of its motion later.
 */
export const PAGE_END_DELAY_MS = 33;

/** The keyboard's progress by ms since it started to move, measured on iOS 26.5 (kb31f, the hide;
 *  the rise matches it within 0.03). */
export const KEYBOARD_MOTION: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [16, 0.136],
  [33, 0.277],
  [49, 0.418],
  [66, 0.543],
  [83, 0.649],
  [99, 0.734],
  [116, 0.801],
  [133, 0.852],
  [149, 0.891],
  [166, 0.921],
  [183, 0.942],
  [199, 0.958],
  [216, 0.97],
  [233, 0.978],
  [249, 0.985],
  [266, 0.989],
  [283, 0.992],
  [299, 0.995],
  [316, 0.996],
  [333, 0.997],
  [349, 0.998],
  [366, 0.999],
  [383, 1],
];

/** The keyboard's progress `ms` after it started to move. */
export function keyboardProgressAt(ms: number): number {
  if (ms <= 0) return 0;
  for (let index = 1; index < KEYBOARD_MOTION.length; index += 1) {
    const [time, progress] = KEYBOARD_MOTION[index];
    if (ms <= time) {
      const [lastTime, lastProgress] = KEYBOARD_MOTION[index - 1];
      return lastProgress + ((progress - lastProgress) * (ms - lastTime)) / (time - lastTime);
    }
  }
  return 1;
}

/** The ms since it started at which the keyboard had reached `progress`. */
export function keyboardTimeAt(progress: number): number {
  if (progress <= 0) return 0;
  for (let index = 1; index < KEYBOARD_MOTION.length; index += 1) {
    const [time, reached] = KEYBOARD_MOTION[index];
    if (progress <= reached) {
      const [lastTime, lastReached] = KEYBOARD_MOTION[index - 1];
      return lastTime + ((time - lastTime) * (progress - lastReached)) / (reached - lastReached);
    }
  }
  return KEYBOARD_MOTION[KEYBOARD_MOTION.length - 1][0];
}

/**
 * How far ahead of now the bar is placed while the keyboard moves: one frame. WebKit draws the page
 * about a frame behind the native layer the keyboard moves on, so a bar placed for "now" trailed it
 * by 1.5–2 frames in the fast part — 60–93 pt on the rise, and the hide's last frame (kb31i/kb21i,
 * measured on iOS 26.5). Measured again with the probe at 16.7 and 33 ms.
 */
export const KEYBOARD_LEAD_MS = 16.7;

/** The web view's height before the keyboard rose; kept while the keyboard is down. */
let fullHeight = 0;
/** A move: from one keyboard top to another, started (`start`) once the keyboard is seen moving;
 *  `seen` once the shell has said where the keyboard really is (`keyboardMoving`). */
let motion: {
  from: number;
  to: number;
  height: number;
  start: number | null;
  seen: boolean;
  /** I-1556: the page's bottom was seen between the move's two ends: the shell moves it with the
   *  keyboard, frame by frame, so the page's bottom is where the keyboard is. */
  paged: boolean;
  /** I-1556: the motion was started by the web's own bound (`KEYBOARD_MOVING_WAIT_MS`), a guess the
   *  page's end (`startFromPageEnd`) or the shell's word improves on. */
  fallback: boolean;
  /** I-1556 (IU-3b K6): the focused field has been revealed for this rise's landing. */
  revealed?: boolean;
} | null = null;
let motionFrame = 0;
let motionWait: ReturnType<typeof setTimeout> | null = null;
let goneTimer: ReturnType<typeof setTimeout> | null = null;
/** Whether the web view has been seen shorter than its full height while the keyboard is up: only
 *  a web view that was shortened can be taken as back at full height. A page whose height never
 *  moves for the keyboard says nothing by its height being full. */
let webViewShortened = false;

function keyboardTopAt(now: number): number {
  if (!motion) return fullHeight;
  if (motion.start === null) return motion.from;
  return motion.from + (motion.to - motion.from) * keyboardProgressAt(now - motion.start);
}

function followMovedWebView(): void {
  const now = performance.now();
  const anchor = window.innerHeight;
  // Placed for when this frame is on screen, a frame from now (`KEYBOARD_LEAD_MS`).
  const top = keyboardTopAt(now + KEYBOARD_LEAD_MS);
  const moving =
    motion &&
    (motion.start === null || keyboardProgressAt(now + KEYBOARD_LEAD_MS - motion.start) < 1);
  // A hide that has landed leaves nothing to follow: the keyboard's top is the web view's bottom, and
  // the frame it lands in is published as a keyboard-down one (U-0019: it was published with the
  // pan of that frame and the motion dropped after, so an offset WebKit reported as the hide ended
  // stayed in the bar's place until the next event).
  if (!moving && motion?.height === 0) {
    motion = null;
    webViewShortened = false;
  }
  const covered = motion?.height ?? 0;
  // I-1235: placed against the visual viewport. As the web view shrinks WebKit can pan the layout
  // viewport under it for a frame (83 pt, kb21k), and a bar placed in layout coordinates went with it.
  // I-1325: only while the keyboard is up or moving. With it down the one offset WebKit reports is
  // the rubber band's past the page's end, while the bar stays on the screen: added, it dropped the
  // composer by the bounce (about 20 pt). The content bounces, the bar stays put.
  const pan = motion ? (window.visualViewport?.offsetTop ?? 0) : 0;
  /*
    I-1556: never below the page's laid-out bottom. WebKit paints only inside the web view's laid-out
    height and draws a fixed element only inside the layout viewport, so a bar placed below it is not
    drawn at all. The shell keeps that bottom on the keyboard's top frame by frame, so this binds only
    where it does not: an older shell that shrank the web view at once at the start of the rise left
    the bar under the page's bottom, undrawn, for about 11 frames (180 ms).
  */
  /*
    I-1556 (the hide): a hide never leaves the bar above the page's bottom, where the page's rows
    then showed between the bar and the keyboard (joker, list 8: about 120 ms, the bar held at the
    raised place until the shell's word that the keyboard had started). A shell that moves the page's
    bottom with the keyboard each frame (`paged`) puts it where the keyboard is, so the bar rides
    that bottom down with no word needed: on it, neither above it (rows between) nor below it, where
    WebKit draws nothing (the rise's rule). A shell that returns the full height at once says nothing
    by it: there the motion alone places the bar.
  */
  const hiding = motion !== null && motion.to > motion.from;
  /*
    I-1556 (list 11): and on such a hide the bar is not put on the page's end either, once the motion
    is known (from the shell's word or from the page's end, `startFromPageEnd`): the page sees its
    end two frames after the shell set it, so a bar on it trailed the keyboard by about 67 ms with
    the page's rows beneath it. It is placed where the keyboard was a frame ago: the web view's
    bottom is at the keyboard's last frame, and a bar at the keyboard's place now would be cut off
    by it (below the bottom WebKit draws nothing). Until the motion is known the page's end is the
    only thing that says the keyboard is moving.
  */
  const drawnTop = hiding
    ? motion?.paged
      ? motion.start === null
        ? anchor
        : keyboardTopAt(now - KEYBOARD_LEAD_MS)
      : top
    : Math.min(top, anchor);
  publish({
    inset: covered > 0 ? Math.round(anchor - top) : 0,
    transition: 'none',
    covered,
    place: { full: Math.round(fullHeight + pan), keyboardTop: Math.round(drawnTop + pan) },
  });
  motionFrame = moving ? requestAnimationFrame(followMovedWebView) : 0;
  if (!moving && motion && motion.height > 0 && !motion.revealed) {
    motion.revealed = true;
    revealFocusedField();
  }
  // A move that lands is the first moment a web view already at full height can be called a gone
  // keyboard: its resize came while the move was still running.
  if (!moving) checkKeyboardGone();
}

/*
  I-1556 (IU-3b K6): the shell holds the page's offset while it follows a rise, because WebKit's own
  reveal of the focused field is an animated scroll of the app's scroll view that the page's fixed
  layers do not follow (the back row drawn 38 pt low for six frames, joker 0.5.7). So once the
  keyboard has landed, the page reveals a field in its flow that the keyboard now covers, itself:
  `scrollIntoView` to the nearest edge, which keeps the docked bar's `scroll-padding-bottom` clear.
  A field in a docked bar or any other fixed layer moves with the keyboard and needs nothing.
*/
export function revealFocusedField(): void {
  const focused = document.activeElement;
  if (!isEditable(focused) || insideFixedLayer(focused)) return;
  const rect = focused.getBoundingClientRect();
  // The docked bar's clearance (`DockedBar`'s `scroll-padding-bottom`): a field under the bar is covered.
  const clear = parseFloat(getComputedStyle(document.documentElement).scrollPaddingBottom) || 0;
  if (rect.bottom <= window.innerHeight - clear && rect.top >= 0) return;
  focused.scrollIntoView({ block: 'nearest' });
}

function insideFixedLayer(element: Element): boolean {
  for (let node: Element | null = element; node; node = node.parentElement) {
    if (node.matches('[data-docked-bar]')) return true;
    const position = getComputedStyle(node).position;
    if (position === 'fixed' || position === 'sticky') return true;
  }
  return false;
}

/*
  U-0019: the web view's own height is the one thing that says where the keyboard is. The shell pins
  its bottom to the keyboard's top, so a web view back at its full height means no keyboard — whatever
  the events said last. The events can miss: a screen that leaves with the keyboard up hears no hide
  (`viewDidDisappear` returns the web view to full height and sends nothing), and a height that came
  back while a move was still running finds nothing after it. The bar was then left on the keyboard
  that had been (471 in an 874 view, with the page drawn to 874 under it: Jay's bar in the middle of
  the conversation).

  So a web view at its full height, with the keyboard's last move landed and no word of a new one
  within `KEYBOARD_GONE_WAIT_MS`, is a keyboard that is down. The wait is for the shell's own order:
  the hide's `keyboard` can reach the page a moment after the height does, and the bar then rides
  the keyboard down rather than going at once.
*/

/** How long a web view back at full height waits for the shell's word of the keyboard before the
 *  keyboard is taken as gone: past a bridge message's usual delay, short of a person noticing. */
export const KEYBOARD_GONE_WAIT_MS = 100;

function cancelGone(): void {
  if (goneTimer) clearTimeout(goneTimer);
  goneTimer = null;
}

/** Whether the keyboard's last move to a height above 0 has landed. */
function landedUp(): boolean {
  return (
    !!motion &&
    motion.height > 0 &&
    motion.start !== null &&
    keyboardProgressAt(performance.now() + KEYBOARD_LEAD_MS - motion.start) >= 1
  );
}

/** The web view's height changed or a move landed: is the keyboard the page thinks is up gone? */
function checkKeyboardGone(): void {
  cancelGone();
  if (!webViewMoves || !webViewShortened || !landedUp() || window.innerHeight < fullHeight) return;
  goneTimer = setTimeout(() => {
    goneTimer = null;
    if (!landedUp() || window.innerHeight < fullHeight) return;
    cancelAnimationFrame(motionFrame);
    motion = null;
    webViewShortened = false;
    followMovedWebView();
  }, KEYBOARD_GONE_WAIT_MS);
}

/**
 * The keyboard has started to move: `progress` of the way already, or 0 when the web started it.
 * A `keyboardMoving` that arrives after the web has started the motion itself (the bridge late on a
 * busy phone, past `KEYBOARD_MOVING_WAIT_MS`) re-anchors it to the keyboard's real start: the
 * fallback's guess ran a whole start ahead or behind, the same for every frame of the rise, and the
 * bar is back on the keyboard on the next paint.
 */
function startMotion(progress: number, seen: boolean): void {
  if (!motion || motion.seen || (motion.start !== null && !seen)) return;
  motion.seen = seen;
  motion.fallback = !seen;
  if (motionWait) clearTimeout(motionWait);
  motionWait = null;
  motion.start = performance.now() - keyboardTimeAt(progress);
  // Placed for the very next paint, not a frame later.
  cancelAnimationFrame(motionFrame);
  followMovedWebView();
}

/**
 * I-1556 (list 11): the keyboard has begun to move and the page has seen it, by its own height: a
 * shell that moves the web view's bottom with the keyboard puts the keyboard's top there. The motion
 * does not wait for `keyboardMoving`, whose message came after the keyboard had covered the bar's
 * resting place (about 200 ms of no bar on the simulator, a rise). The shell's word still re-anchors
 * it when it comes (`seen` stays false). The page sees a height `PAGE_END_DELAY_MS` after the shell
 * set it, so the motion is that far on.
 */
function startFromPageEnd(): void {
  if (!motion || motion.seen || (motion.start !== null && !motion.fallback)) return;
  const travel = motion.to - motion.from;
  const progress = (window.innerHeight - motion.from) / travel;
  if (!(progress > 0.005 && progress < 1)) return;
  if (motionWait) clearTimeout(motionWait);
  motionWait = null;
  motion.start = performance.now() - keyboardTimeAt(progress) - PAGE_END_DELAY_MS;
  motion.fallback = false;
}

function followMovedWebViewEvent(event: IShellKeyboardEvent): void {
  cancelAnimationFrame(motionFrame);
  cancelGone();
  if (motionWait) clearTimeout(motionWait);
  const height = Math.max(0, Math.round(event.height));
  // I-1229: a rise with no duration (a hardware keyboard's software keyboard, in one step): the
  // shell laid the web view out at its new bottom at once, so the bar goes there at once rather
  // than playing a motion below the web view. A hide says 0 too and still slides: it keeps it.
  if (event.duration === 0 && height > (motion?.height ?? 0)) {
    const to = fullHeight - height;
    motion = {
      from: to,
      to,
      height,
      start: performance.now(),
      seen: true,
      paged: false,
      fallback: false,
    };
    motionWait = null;
    followMovedWebView();
    return;
  }
  // From where the bar was last drawn.
  const from = motion ? keyboardTopAt(performance.now() + KEYBOARD_LEAD_MS) : fullHeight;
  motion = {
    from,
    to: fullHeight - height,
    height,
    start: null,
    seen: false,
    paged: false,
    fallback: false,
  };
  motionWait = setTimeout(() => startMotion(0, false), KEYBOARD_MOVING_WAIT_MS);
  followMovedWebView();
}

function followKeyboardMoving(event: IShellKeyboardMovingEvent): void {
  if (!motion || Math.round(event.height) !== motion.height) return;
  startMotion(Math.min(1, Math.max(0, event.progress)), true);
}

/**
 * The web view's height before the keyboard: the tallest it has been at this width. The keyboard
 * only ever shortens it, and its new height can reach the page before or after the event does.
 */
let fullWidth = 0;
function noteFullHeight(): void {
  if (window.innerWidth !== fullWidth) {
    fullWidth = window.innerWidth;
    fullHeight = window.innerHeight;
  } else {
    fullHeight = Math.max(fullHeight, window.innerHeight);
  }
}

/** The web view's height changed with no event to animate: where the keyboard's top now is. */
function followMovedWebViewResize(): void {
  noteFullHeight();
  if (motion && window.innerHeight < fullHeight) webViewShortened = true;
  if (
    motion &&
    window.innerHeight > Math.min(motion.from, motion.to) + 0.5 &&
    window.innerHeight < Math.max(motion.from, motion.to) - 0.5
  ) {
    motion.paged = true;
    startFromPageEnd();
  }
  if (!motion) {
    publish({ ...snapshot, place: { full: fullHeight, keyboardTop: fullHeight } });
    return;
  }
  // Placed again in this same frame, whether or not the motion's own frames are running: the new
  // height is laid out now.
  cancelAnimationFrame(motionFrame);
  followMovedWebView();
  checkKeyboardGone();
}

function followShell(event: IShellKeyboardEvent): void {
  if (webViewMoves) {
    followMovedWebViewEvent(event);
    return;
  }
  // The visual viewport reports the same height once the keyboard has landed; until then (and a
  // little after, for a report that arrives late) the move keeps the keyboard's own curve.
  shellCurveUntil = performance.now() + event.duration * 1000 + 300;
  const height = Math.max(0, Math.round(event.height));
  // Less any pan WebKit has already made for it (I-1145): when the visual viewport reported first,
  // the layout viewport is already up by that much, and the keyboard's full height would lift the
  // bar a second time. A pan made after this event reaches `followVisualViewport` as a scroll.
  publish({
    inset: Math.max(0, height - visualViewportPan()),
    covered: height,
    transition: transitionOfKeyboardEvent(event),
    place: null,
  });
}

function isEditable(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement)) return false;
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element.isContentEditable
  );
}

function attach(): () => void {
  webViewMoves = isShellRuntime() && hasShellCapability('keyboardResize');
  fullWidth = 0;
  noteFullHeight();
  if (webViewMoves) {
    window.addEventListener('resize', followMovedWebViewResize);
    publish({ ...snapshot, place: { full: fullHeight, keyboardTop: fullHeight } });
  }
  const viewport = window.visualViewport;
  viewport?.addEventListener('resize', followVisualViewport);
  viewport?.addEventListener('scroll', followVisualViewport);
  const stopShell = onShellEvent((event) => {
    if (event.type === 'keyboard') followShell(event);
    if (event.type === 'keyboardMoving' && webViewMoves) followKeyboardMoving(event);
  });

  // Mobile Safari's dismissal; the app has `keyboardDismissMode = .onDrag` and needs none.
  let dragStartY: number | null = null;
  const onTouchStart = (event: TouchEvent) => {
    const focused = document.activeElement;
    dragStartY =
      snapshot.covered > 0 && isEditable(focused) && !focused.contains(event.target as Node)
        ? (event.touches[0]?.clientY ?? null)
        : null;
  };
  const onTouchMove = (event: TouchEvent) => {
    if (dragStartY === null) return;
    const y = event.touches[0]?.clientY;
    if (y === undefined || y - dragStartY <= DISMISS_DRAG_PX) return;
    dragStartY = null;
    const focused = document.activeElement;
    if (isEditable(focused)) focused.blur();
  };
  const dismissesOnDrag = !isShellRuntime();
  if (dismissesOnDrag) {
    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true, capture: true });
  }

  followVisualViewport();
  return () => {
    viewport?.removeEventListener('resize', followVisualViewport);
    viewport?.removeEventListener('scroll', followVisualViewport);
    window.removeEventListener('resize', followMovedWebViewResize);
    cancelGone();
    cancelAnimationFrame(motionFrame);
    motionFrame = 0;
    if (motionWait) clearTimeout(motionWait);
    motionWait = null;
    stopShell();
    if (dismissesOnDrag) {
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
    }
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!detach) detach = attach();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && detach) {
      detach();
      detach = null;
    }
  };
}

const SERVER_SNAPSHOT: IKeyboardInset = {
  inset: 0,
  transition: VISUAL_VIEWPORT_TRANSITION,
  covered: 0,
  place: null,
};

/** The keyboard's inset and the transition that follows it; re-renders when either changes. */
export function useKeyboardInset(): IKeyboardInset {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => SERVER_SNAPSHOT,
  );
}

/** Tests only: forget the last reading and every listener. */
export function resetKeyboardInsetForTests(): void {
  detach?.();
  detach = null;
  listeners.clear();
  shellCurveUntil = 0;
  webViewMoves = false;
  motion = null;
  if (motionWait) clearTimeout(motionWait);
  motionWait = null;
  cancelGone();
  webViewShortened = false;
  fullHeight = 0;
  snapshot = { inset: 0, transition: VISUAL_VIEWPORT_TRANSITION, covered: 0, place: null };
}
